import { Component, ReactNode, createRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { IllustrationPictogramNames } from '@cbhq/cds-common';

import { ERRORS } from '@/app/utils/errors';
import { ErrorBoundaryContents } from '@/components/ErrorBoundary/ErrorBoundaryContents';

export type ErrorFallbackProps = {
  error?: Error;
  errorHash?: string;
};

type ErrorBoundaryProps = {
  // Display a border around the standard error boundary
  bordered?: boolean;
  children: ReactNode;
  // Display a custom fallback component — negates the affects of 'fill' and 'height'
  fallback?: React.FC<ErrorFallbackProps>;
  // Fill the ancestor container with the error boundary, if a fallback is not used
  fill?: boolean;
  // Custom error message to display with core-red compliant styling
  message?: string;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errorHash?: string;
  pictogram?: IllustrationPictogramNames;
  title: ReactNode;
  body: ReactNode;
};

// Generate a stable short hash
function getErrorHash(error: Error): string {
  const str = error.stack ?? error.message;
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    const char = str.charCodeAt(i);

    hash = (hash << 5) - hash + char;

    hash &= hash;
  }
  return new Uint32Array([hash])[0].toString(36);
}

export function isKnownError(error: Error): boolean {
  return ERRORS.includes(error?.message);
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private childRef = createRef<HTMLDivElement>();

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      errorHash: undefined,
      pictogram: undefined,
      title: undefined,
      body: undefined,
    };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorHash: getErrorHash(error) };
  }

  componentDidCatch(error: Error) {
    // Conveniently display custom error messages for known errors that have been
    // explicitly thrown. A typical use-case is when multiple GraphQL fields can
    // come back null, and we want full control over which message is displayed
    // in the outer-most error boundary.
    if (isKnownError(error)) {
      this.setState({ title: error.message });
    }

    try {
      // If the error has changed, update the hash
      if (error !== this.state.error) {
        const errorHash = getErrorHash(error);
        this.setState({ errorHash });
      }

      // Report render errors to Datadog RUM for visibility
      datadogRum.addError(error, {
        source: 'ErrorBoundary',
        errorHash: getErrorHash(error),
        isKnownError: isKnownError(error),
      });
    } catch (reportingError) {
      console.error('Error reporting error!!!!', reportingError);
    }
  }

  render() {
    const { body, error, errorHash, hasError, pictogram, title } = this.state;
    const { bordered, children, fallback, fill, message } = this.props;

    const Fallback = fallback;

    if (hasError && error) {
      // Render custom fallback UI
      if (Fallback) {
        return <Fallback error={error} errorHash={errorHash} />;
      }

      // Measure the height of the child content before showing the error UI
      const minHeight = this.childRef.current?.getBoundingClientRect()?.height;

      return (
        <ErrorBoundaryContents
          bordered={bordered}
          body={body}
          errorHash={errorHash}
          fill={fill}
          minHeight={minHeight}
          pictogram={pictogram}
          title={message ?? title}
        />
      );
    }

    return (
      <div ref={this.childRef} style={{ width: '100%' }}>
        {children}
      </div>
    );
  }
}

export default ErrorBoundary;
